import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ArrowLeft,
  Search,
  X,
  User,
  Phone,
  MapPin,
  Calendar,
  FileDigit,
  Building2,
  Receipt,
  IndianRupee,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { getBaseUrl } from '@/lib/api';
import { getLocalDateString } from '@/lib/utils';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { PRIVILEGE } from '@/lib/privilegeConstants';

interface Product {
  id: string;
  name: string;
  quantity: number;
  unitCost: number;
  discount: number;
  sgst: number;
  cgst: number;
  igst: number;
  cess?: number;
  taxableValue: number;
  totalAmount: number;
  serialNumber?: string;
  returnQty?: number;
  productCode?: number;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  companyName: string;
  createdOn: string;
  name: string;
  buyerName: string;
  customerCompany: string;
  phone: string;
  email?: string;
  discount: number;
  amount: number;
  location: string;
  products: Product[];
}

interface RefundsScreenProps {
  token: string;
  onBack: () => void;
}

const REFUNDS_FILTERS_KEY = 'refundsFilters';

const RefundsScreen: React.FC<RefundsScreenProps> = ({ token, onBack }) => {
  const navigate = useNavigate();
  const { hasPrivilege } = useUserPrivileges();
  const showAllBranches = hasPrivilege(PRIVILEGE.ALL_BRANCHES);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [startDate, setStartDate] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const s = sessionStorage.getItem(REFUNDS_FILTERS_KEY);
        if (s) {
          const p = JSON.parse(s);
          if (p.startDate) return p.startDate;
        }
      } catch {}
    }
    return '';
  });
  const [endDate, setEndDate] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const s = sessionStorage.getItem(REFUNDS_FILTERS_KEY);
        if (s) {
          const p = JSON.parse(s);
          if (p.endDate) return p.endDate;
        }
      } catch {}
    }
    return '';
  });
  const [refreshKey, setRefreshKey] = useState(0);

  // Set default dates to today when no saved filters exist
  useEffect(() => {
    try {
      const s = sessionStorage.getItem(REFUNDS_FILTERS_KEY);
      if (s) return;
    } catch {}
    const today = getLocalDateString();
    setStartDate(today);
    setEndDate(today);
  }, []);

  // Also handle refresh from parent component
  useEffect(() => {
    const handleRefresh = () => {
      console.log('🔄 Refreshing RefundsScreen data from parent component');
      setRefreshKey(prev => prev + 1);
    };

    // Listen for refresh events
    window.addEventListener('refreshRefunds', handleRefresh);
    return () => window.removeEventListener('refreshRefunds', handleRefresh);
  }, []);

  const [companyFilter, setCompanyFilter] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const s = sessionStorage.getItem(REFUNDS_FILTERS_KEY);
        if (s) {
          const p = JSON.parse(s);
          if (p.companyFilter) return p.companyFilter;
        }
      } catch {}
    }
    return 'All';
  });
  const [locationFilter, setLocationFilter] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const s = sessionStorage.getItem(REFUNDS_FILTERS_KEY);
        if (s) {
          const p = JSON.parse(s);
          if (p.locationFilter) return p.locationFilter;
        }
      } catch {}
    }
    return 'All';
  });
  const [pageSize, setPageSize] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const s = sessionStorage.getItem(REFUNDS_FILTERS_KEY);
        if (s) {
          const p = JSON.parse(s);
          if (typeof p.pageSize === 'number' && [10, 20, 50, 100].includes(p.pageSize)) return p.pageSize;
        }
      } catch {}
    }
    return 10;
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [companies, setCompanies] = useState<{Id: any, Name: string}[]>([]);
  const [locations, setLocations] = useState<{ID: any, Name: string}[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [mobileFiltersExpanded, setMobileFiltersExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobileInvoices, setMobileInvoices] = useState<Invoice[]>([]);
  const [mobilePageIndex, setMobilePageIndex] = useState(0);
  const [mobileHasMore, setMobileHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const MOBILE_PAGE_SIZE = 25;

  // Persist filters to sessionStorage so they survive navigation
  useEffect(() => {
    if (!startDate || !endDate) return;
    try {
      sessionStorage.setItem(REFUNDS_FILTERS_KEY, JSON.stringify({
        startDate,
        endDate,
        companyFilter,
        locationFilter,
        pageSize
      }));
    } catch {}
  }, [startDate, endDate, companyFilter, locationFilter, pageSize]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = () => setIsMobile(mq.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Reset to page 0 when filters or page size change
  useEffect(() => {
    setCurrentPage(0);
    setMobilePageIndex(0);
    setMobileInvoices([]);
    setMobileHasMore(true);
  }, [startDate, endDate, companyFilter, locationFilter, pageSize]);

  // Fetch companies data
  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const baseUrl = getBaseUrl();
        const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        const response = await fetch(`${baseUrl}/api/Companies`, {
          headers: {
            Authorization: authToken,
            Accept: 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const companiesList = Array.isArray(data?.data) ? data.data : [];
          setCompanies(companiesList);
        } else {
          console.error('Failed to fetch companies');
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [token]);

  // Fetch locations data
  useEffect(() => {
    const fetchLocations = async () => {
      setLoadingLocations(true);
      try {
        const baseUrl = getBaseUrl();
        const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        const response = await fetch(`${baseUrl}/api/Locations`, {
          headers: {
            Authorization: authToken,
            Accept: 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const locationsList = Array.isArray(data) ? data : (data?.data || []);
          setLocations(locationsList);
        } else {
          console.error('Failed to fetch locations');
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [token]);

  // When user does not have "All Branches" privilege, default company/location to first option instead of "All"
  useEffect(() => {
    if (showAllBranches) return;
    if (companyFilter === 'All' && companies.length > 0) {
      setCompanyFilter(companies[0].Name);
    }
    if (locationFilter === 'All' && locations.length > 0) {
      setLocationFilter(locations[0].Name);
    }
  }, [showAllBranches, companyFilter, locationFilter, companies, locations]);

  // Mock data - replace with actual API call
  const mockInvoices: Invoice[] = [
    {
      id: '1',
      invoiceNumber: 'INV-2024-001',
      companyName: 'Tech Solutions Ltd',
      createdOn: '2024-01-15',
      name: 'Tech Solutions Ltd',
      buyerName: 'John Doe',
      customerCompany: 'Tech Solutions Ltd',
      phone: '+1 234-567-8900',
      email: 'john@example.com',
      discount: 50.00,
      amount: 1250.00,
      location: 'New York, USA',
      products: [
        {
          id: 'p1',
          name: 'Laptop Pro 15"',
          quantity: 2,
          unitCost: 500.00,
          discount: 50.00,
          sgst: 45.00,
          cgst: 45.00,
          igst: 0,
          taxableValue: 900.00,
          totalAmount: 990.00,
          serialNumber: 'LP15-001, LP15-002'
        },
        {
          id: 'p2',
          name: 'Wireless Mouse',
          quantity: 1,
          unitCost: 25.00,
          discount: 0,
          sgst: 2.25,
          cgst: 2.25,
          igst: 0,
          taxableValue: 25.00,
          totalAmount: 29.50
        }
      ]
    },
    {
      id: '2',
      invoiceNumber: 'INV-2024-002',
      companyName: 'Digital Marketing Co',
      createdOn: '2024-01-20',
      name: 'Digital Marketing Co',
      buyerName: 'Jane Smith',
      customerCompany: 'Digital Marketing Co',
      phone: '+1 234-567-8901',
      email: 'jane@example.com',
      discount: 100.00,
      amount: 850.00,
      location: 'Los Angeles, USA',
      products: [
        {
          id: 'p3',
          name: 'Software License',
          quantity: 1,
          unitCost: 800.00,
          discount: 100.00,
          sgst: 35.00,
          cgst: 35.00,
          igst: 0,
          taxableValue: 700.00,
          totalAmount: 770.00
        }
      ]
    }
  ];

  useEffect(() => {
    const shouldFetch = startDate && endDate;
    if (!shouldFetch) return;
    
    // Determine CompanyID and LocationID values
    const companyID = companyFilter === 'All' ? 0 : 
      companies.find(c => c.Name === companyFilter)?.Id || 0;
    const locationID = locationFilter === 'All' ? 0 : locationFilter;
    
    console.log('=== REFUNDS API CALL DEBUG ===');
    console.log('Company Filter:', companyFilter);
    console.log('Location Filter:', locationFilter);
    console.log('CompanyID:', companyID);
    console.log('LocationID:', locationID);
    
    const baseUrl = getBaseUrl();
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    const useMobileLogic = isMobile;
    const pageStart = useMobileLogic ? mobilePageIndex * MOBILE_PAGE_SIZE : currentPage * pageSize;
    const maxRecords = useMobileLogic ? MOBILE_PAGE_SIZE : pageSize;
    const url = `${baseUrl}/api/InvoicesBetweenDates?StartDate=${startDate}&EndDate=${endDate}&CompanyID=${companyID}&LocationID=${locationID}&PageStart=${pageStart}&MaxRecords=${maxRecords}`;
    
    console.log('API URL:', url);
    console.log('==========================');
    const isLoadMore = useMobileLogic && mobilePageIndex > 0;
    if (!isLoadMore) setIsLoading(true);
    else setIsLoadingMore(true);
    fetch(url, {
      headers: {
        Authorization: authToken,
        Accept: 'application/json'
      }
    })
      .then(async (res) => {
        console.log('=== API RESPONSE DEBUG ===');
        console.log('Response Status:', res.status);
        console.log('Response OK:', res.ok);
        console.log('Response Headers:', Object.fromEntries(res.headers.entries()));
        
        const text = await res.text();
        console.log('Response Text:', text);
        console.log('======================');
        
        let data: any;
        try {
          data = JSON.parse(text);
        } catch {
          data = [];
        }
        const arr = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        const total = Number(data?.total ?? data?.Total ?? data?.totalRecords ?? data?.TotalRecords ?? data?.totalCount ?? data?.TotalCount ?? data?.count ?? data?.Count ?? null);
        if (total != null && !isNaN(total)) {
          setTotalRecords(total);
        } else {
          setTotalRecords(pageStart + arr.length);
        }
        const mapped: Invoice[] = arr.map((item: any, idx: number) => ({
          id: String(item.InvoiceId ?? item.Invoiceid ?? item.id ?? idx + 1),
          invoiceNumber: String(item.InvSeqNo ?? item['Invoice No'] ?? item.InvRefNo ?? item.InvoiceNo ?? item.InvoiceRefNo ?? '-'),
          companyName: String(item.CompanyName ?? item['Customer Company'] ?? item.CustomerCompany ?? item.customerCompany ?? ''),
          createdOn: String(item['Invoice Date'] ?? item.CreatedOn ?? item.InvDateGen ?? item.CreatedDate ?? ''),
          name: String(item.CustomerName ?? item.ShippingName ?? ''),
          buyerName: String(item.BuyerName ?? ''),
          customerCompany: String(item.CustomerCompany ?? item['Customer Company'] ?? ''),
          phone: String(item['Customer Phone'] ?? item.Mobile ?? item.Mobile1 ?? item.Phone ?? ''),
          email: String(item.Email ?? item.emailid ?? ''),
          discount: Number(item.Discount ?? item.ldiscount ?? 0),
          amount: Number(item.InvoicedAmount ?? item.TotalCost ?? item.PayableAmount ?? 0),
          location: String(item.Location ?? item.shippingstate ?? item.CmpState ?? ''),
          products: Array.isArray(item.Products)
            ? item.Products.map((p: any, pidx: number) => ({
              id: String(p.id ?? pidx + 1),
              name: String(p.Title ?? p.ProductName ?? p.name ?? '-'),
              quantity: Number(p.Quantity ?? p.quantity ?? 0),
              unitCost: Number(p.UnitCost ?? p.unitcost ?? 0),
              discount: Number(p.Discount ?? p.discount ?? 0),
              sgst: Number(p.SGST ?? p.Sgst ?? 0),
              cgst: Number(p.CGST ?? p.Cgst ?? 0),
              igst: Number(p.IGST ?? p.Igst ?? 0),
              cess: Number(p.Cess ?? p.cess ?? 0),
              taxableValue: Number(p.TaxableValue ?? p.taxableValue ?? 0),
              totalAmount: Number(p.TotalCost ?? p.totalcost ?? 0),
              serialNumber: String(p.SerialNumber ?? '')
            }))
            : []
        }));

        // Debug log to verify correct InvoiceId mapping
        console.log('=== INVOICES MAPPED ===');
        console.log('Total invoices:', mapped.length);
        console.log('Sample invoice IDs:', mapped.slice(0, 3).map(inv => ({
          id: inv.id,
          invoiceNumber: inv.invoiceNumber,
          companyName: inv.companyName
        })));
        console.log('=========================');

        if (useMobileLogic && mobilePageIndex === 0) {
          setMobileInvoices(mapped);
          setMobileHasMore(mapped.length >= MOBILE_PAGE_SIZE);
        } else if (useMobileLogic && mobilePageIndex > 0) {
          setMobileInvoices(prev => [...prev, ...mapped]);
          setMobileHasMore(mapped.length >= MOBILE_PAGE_SIZE);
        } else {
          setInvoices(mapped);
        }
      })
      .catch(() => {
        if (useMobileLogic) setMobileInvoices([]);
        else setInvoices([]);
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadingMore(false);
      });
  }, [startDate, endDate, companyFilter, locationFilter, token, refreshKey, currentPage, pageSize, isMobile, mobilePageIndex]);

  const loadMoreMobile = useCallback(() => {
    if (!isMobile || isLoadingMore || !mobileHasMore) return;
    setIsLoadingMore(true);
    setMobilePageIndex(prev => prev + 1);
  }, [isMobile, isLoadingMore, mobileHasMore]);

  const sourceInvoices = isMobile ? mobileInvoices : invoices;
  const filteredInvoices = useMemo(() => {
    const query = searchQuery.toLowerCase();
    if (!query) return sourceInvoices;
    
    return sourceInvoices.filter(invoice => {
      return invoice.invoiceNumber.toLowerCase().includes(query) ||
        invoice.companyName.toLowerCase().includes(query) ||
        invoice.name.toLowerCase().includes(query) ||
        invoice.buyerName.toLowerCase().includes(query) ||
        invoice.customerCompany.toLowerCase().includes(query) ||
        invoice.phone.toLowerCase().includes(query) ||
        (invoice.email && invoice.email.toLowerCase().includes(query));
    });
  }, [sourceInvoices, searchQuery]);

  const handleScroll = useCallback(() => {
    if (!isMobile || !mobileHasMore || isLoadingMore) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 150;
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      loadMoreMobile();
    }
  }, [isMobile, mobileHasMore, isLoadingMore, loadMoreMobile]);

  const openInvoiceDetails = (invoice: Invoice) => {
    navigate('/refunds/invoice', { state: { invoice } });
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden page-container bg-gradient-to-br from-slate-50 via-white to-slate-100/80">
      {/* Page Header - Glassmorphism */}
      <div className="flex-shrink-0 page-header px-4 py-4 md:px-6 md:py-5 border-b border-white/40 bg-white/70 backdrop-blur-xl shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="h-9 w-9 md:h-10 md:w-10 rounded-xl hover:bg-white/80 transition-all duration-300"
            >
              <ArrowLeft className="h-4 w-4 md:h-5 md:w-5 text-slate-600" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Refunds / Returns
              </h1>
              <p className="text-sm text-slate-500 hidden md:block mt-0.5">
                Search and select invoices to process returns
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Page Filters - Glass style */}
      <div className="border-b border-slate-200/60 px-4 py-3 md:px-6 md:py-4 flex-shrink-0 page-filters bg-white/50 backdrop-blur-sm">
        {/* Mobile Layout - collapsible, search always visible */}
        <div className="md:hidden space-y-3">
          <div className="flex items-center gap-2">
            {/* Search bar - always visible */}
            <div className="search-box flex-1 min-w-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by Invoice No, Company, Customer Name, Mobile, Email"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10 h-10 rounded-xl border-slate-200/80 bg-white/80 focus:ring-2 focus:ring-slate-200/50"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            {/* Expand/collapse toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 flex-shrink-0 rounded-xl"
              onClick={() => setMobileFiltersExpanded(!mobileFiltersExpanded)}
              aria-expanded={mobileFiltersExpanded}
            >
              {mobileFiltersExpanded ? <ChevronUp className="h-5 w-5 text-slate-600" /> : <ChevronDown className="h-5 w-5 text-slate-600" />}
            </Button>
          </div>

          {/* Collapsible filters - hidden by default */}
          {mobileFiltersExpanded && (
          <div className="space-y-4 pt-1 border-t border-slate-200/60">
          {/* Date fields - side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div className="date-box">
              <label className="text-[13px] font-medium mb-1 block">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-10"
              />
            </div>
            <div className="date-box">
              <label className="text-[13px] font-medium mb-1 block">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-10"
              />
            </div>
          </div>

          {/* Company dropdown - single row */}
          <div className="date-box">
            <label className="text-[13px] font-medium mb-1 block">Company</label>
            <Select value={companyFilter} onValueChange={setCompanyFilter} disabled={loadingCompanies}>
              <SelectTrigger className="h-10">
                <SelectValue placeholder={loadingCompanies ? "Loading..." : "Select company"} />
              </SelectTrigger>
              <SelectContent>
                {showAllBranches && <SelectItem value="All">All</SelectItem>}
                {companies.map((company, index) => (
                  <SelectItem key={company.Id} value={company.Name}>
                    {company.Name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Location dropdown - single row */}
          <div className="date-box">
            <label className="text-[13px] font-medium mb-1 block">Location</label>
            <Select value={locationFilter} onValueChange={setLocationFilter} disabled={loadingLocations}>
              <SelectTrigger className="h-10">
                <SelectValue placeholder={loadingLocations ? "Loading..." : "Select location"} />
              </SelectTrigger>
              <SelectContent>
                {showAllBranches && <SelectItem value="All">All</SelectItem>}
                {locations.map((location, index) => (
                  <SelectItem key={location.ID} value={location.Name}>
                    {location.Name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          </div>
          )}
        </div>

        {/* Desktop Layout */}
        <div
          className="hidden md:grid gap-4 items-end filter-row"
          style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr' }}
        >
          <div className="search-box">
            <label className="text-[13px] font-medium mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by Invoice No, Company, Customer Name, Mobile, Email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 h-10 md:h-11 rounded-xl border-slate-200/80 bg-white/80 focus:ring-2 focus:ring-slate-200/50"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="date-box">
            <label className="text-[13px] font-medium mb-1 block">Start Date</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-10 md:h-11"
            />
          </div>
          <div className="date-box">
            <label className="text-[13px] font-medium mb-1 block">End Date</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-10 md:h-11"
            />
          </div>
          <div className="date-box">
            <label className="text-[13px] font-medium mb-1 block">Company</label>
            <Select value={companyFilter} onValueChange={setCompanyFilter} disabled={loadingCompanies}>
              <SelectTrigger className="h-10 md:h-11">
                <SelectValue placeholder={loadingCompanies ? "Loading..." : "Select company"} />
              </SelectTrigger>
              <SelectContent>
                {showAllBranches && <SelectItem value="All">All</SelectItem>}
                {companies.map((company, index) => (
                  <SelectItem key={company.Id} value={company.Name}>
                    {company.Name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="date-box">
            <label className="text-[13px] font-medium mb-1 block">Location</label>
            <Select value={locationFilter} onValueChange={setLocationFilter} disabled={loadingLocations}>
              <SelectTrigger className="h-10 md:h-11">
                <SelectValue placeholder={loadingLocations ? "Loading..." : "Select location"} />
              </SelectTrigger>
              <SelectContent>
                {showAllBranches && <SelectItem value="All">All</SelectItem>}
                {locations.map((location, index) => (
                  <SelectItem key={location.ID} value={location.Name}>
                    {location.Name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="date-box">
            <label className="text-[13px] font-medium mb-1 block">Per page</label>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="h-10 md:h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Invoice Scroll Section */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-6 pb-2 invoice-scroll-section"
        onScroll={isMobile ? handleScroll : undefined}
      >
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-white/60 bg-white/60 backdrop-blur-sm overflow-hidden shadow-lg shadow-slate-200/30"
              >
                <div className="p-5">
                  <div className="h-4 bg-slate-200/80 rounded-lg w-3/4 mb-3 shimmer" />
                  <div className="h-3 bg-slate-200/60 rounded-lg w-1/2 shimmer" />
                </div>
              </motion.div>
            ))}
          </div>
        ) : filteredInvoices.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center justify-center py-16 px-4"
          >
            <div className="rounded-full bg-gradient-to-br from-slate-100 to-slate-200/80 p-6 mb-5 shadow-inner">
              <Receipt className="h-14 w-14 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">
              {searchQuery ? 'No invoices found' : 'No invoices available'}
            </h3>
            <p className="text-slate-500 text-sm text-center max-w-sm">
              {searchQuery
                ? 'Try adjusting your search or date range'
                : 'There are no invoices to display for the selected period.'
              }
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {/* Column headers - desktop only */}
            <div className="hidden md:block mb-2 px-4 md:px-5">
              <div className="flex items-center gap-4">
                <div className="flex-1 grid grid-cols-8 gap-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <div className="flex items-center gap-2">Company Name</div>
                  <div className="flex items-center gap-2">Invoice No.</div>
                  <div className="flex items-center gap-1.5">Date</div>
                  <div className="flex items-center gap-1.5">Name</div>
                  <div>Company</div>
                  <div className="flex items-center gap-1.5">Phone</div>
                  <div>Amount</div>
                  <div className="flex items-center gap-1.5">Location</div>
                </div>
                <div className="w-9 flex-shrink-0" aria-hidden />
              </div>
            </div>
            {filteredInvoices.map((invoice, index) => (
              <motion.div
                key={invoice.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: index * 0.04 }}
                className="rounded-2xl border border-white/60 bg-white/70 backdrop-blur-md overflow-hidden shadow-lg shadow-slate-200/40 hover:shadow-xl hover:shadow-slate-300/40 transition-all duration-300 hover:scale-[1.005] cursor-pointer"
                onClick={() => openInvoiceDetails(invoice)}
              >
                <Card className="border-0 bg-transparent shadow-none">
                  <CardContent className="p-0">
                    {/* Desktop Table View */}
                    <div className="hidden md:block">
                      <div className="flex items-center gap-4 p-4 md:p-5 transition-all duration-300 rounded-2xl hover:bg-white/80">
                        <div className="flex-1 grid grid-cols-8 gap-3 text-sm items-center min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                              <Building2 className="h-4 w-4 text-slate-600" />
                            </div>
                            <span className="font-medium text-slate-800 whitespace-normal break-words">{invoice.companyName}</span>
                          </div>
                          <div className="flex items-center gap-2 min-w-0">
                            <FileDigit className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                            <span className="font-mono text-slate-700 truncate">{invoice.invoiceNumber}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Calendar className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                            <span className="truncate">{invoice.createdOn}</span>
                          </div>
                          <div className="flex items-center gap-1.5 min-w-0">
                            <User className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                            <span className="truncate">{invoice.name}</span>
                          </div>
                          <div className="text-slate-600 truncate">{invoice.customerCompany}</div>
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Phone className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                            <span className="truncate">{invoice.phone}</span>
                          </div>
                          <div>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-slate-100 to-slate-50 text-slate-800 border border-slate-200/60">
                              <IndianRupee className="h-3 w-3" />
                              {invoice.amount.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-slate-500 min-w-0">
                            <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                            <span className="truncate">{invoice.location}</span>
                          </div>
                        </div>
                        <div className="ml-2 flex-shrink-0 w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
                          <ChevronRight className="h-4 w-4 text-slate-500" />
                        </div>
                      </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden">
                      <div className="p-4 transition-all duration-300 hover:bg-white/80">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/80 border border-slate-200/60 flex items-center justify-center shadow-sm">
                              <Receipt className="h-5 w-5 text-slate-600" />
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-800 truncate">{invoice.invoiceNumber}</div>
                              <div className="text-xs text-slate-500 flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {invoice.createdOn}
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
                            <ChevronRight className="h-5 w-5 text-slate-500" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <div className="flex items-center gap-1.5 col-span-2">
                            <Building2 className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                            <span className="font-medium text-slate-800 whitespace-normal break-words">{invoice.companyName}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Company</span>
                            <span className="font-medium text-slate-700 truncate">{invoice.customerCompany}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Name :</span>
                            <span className="font-medium text-slate-700 truncate">{invoice.name || invoice.buyerName || '-'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Phone</span>
                            <span className="truncate">{invoice.phone}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Total</span>
                            <span className="font-semibold text-slate-800">₹{invoice.amount.toFixed(2)}</span>
                          </div>
                          <div className="col-span-2 flex items-center gap-1.5 text-slate-500">
                            <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                            <span className="text-xs truncate">{invoice.location}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
            {/* Loading indicator when fetching more - mobile only */}
            {isMobile && filteredInvoices.length > 0 && mobileHasMore && isLoadingMore && (
              <div className="py-4 flex justify-center">
                <div className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-slate-600 animate-spin" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination and hint footer */}
      <div className="flex-shrink-0 border-t border-slate-200/60 bg-white/50 backdrop-blur-sm px-4 py-3 md:px-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">Click an invoice to view details and process return</p>
          {!isLoading && filteredInvoices.length > 0 && (
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                {isMobile
                  ? `Total: ${totalRecords > 0 ? totalRecords : filteredInvoices.length} records`
                  : totalRecords > 0
                    ? `Showing ${currentPage * pageSize + 1}-${Math.min((currentPage + 1) * pageSize, totalRecords)} of ${totalRecords} records`
                    : 'No records'}
              </span>
              {/* Pagination controls - desktop only */}
              {!isMobile && (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-slate-600 min-w-[80px] text-center">
                  Page {currentPage + 1} of {Math.max(1, Math.ceil(totalRecords / pageSize))}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  disabled={(currentPage + 1) * pageSize >= totalRecords}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RefundsScreen;
